import { useProducts } from '@airship-platform/airship-proposal';
import { useCallback, useEffect, useState } from 'react';
import { useMembershipData } from '../../data-offline/features/membershipData/OfflineMembershipDataProvider';
import { useOfflineOptIn } from '../../data-offline/rxdb/useOfflineOptIn';
import {
  useOfflineDbData,
  useOfflineDbReset,
} from '../../data-offline/rxdb/utils/OfflineDbProvider';
import {
  ALL_COLLECTION_NAMES,
  DB_NAME,
} from '../../data-offline/rxdb/utils/rxDbInstance';
import {
  readStorageEstimate,
  type StorageEstimateInfo,
} from '../../data-offline/rxdb/utils/storageEstimate';

export type OfflineCollectionInfo = {
  name: string;
  count: number;
};

const formatCollectionName = (name: string) =>
  name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const useOfflineDiagnostics = () => {
  const data = useOfflineDbData();
  const resetDb = useOfflineDbReset('diagnostics');
  const { isOptedIn, setOptedIn, canOptIn } = useOfflineOptIn();
  const { syncOfflinePricebook, pricebookSync, lookupsSync, modifiersSync } =
    useProducts();
  const { syncMembershipData, membershipDataSync } = useMembershipData();
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [collections, setCollections] = useState<OfflineCollectionInfo[]>([]);
  const [storage, setStorage] = useState<StorageEstimateInfo | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setIsLoading(true);

    data
      .countAll(ALL_COLLECTION_NAMES)
      .then((results) =>
        setCollections(
          results
            .map(({ name, count }) => ({
              name: formatCollectionName(name),
              count,
            }))
            .sort((a, b) => a.name.localeCompare(b.name))
        )
      )
      .finally(() => setIsLoading(false));

    readStorageEstimate().then(setStorage);
  }, [data, refreshKey]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const syncAllNow = useCallback(async () => {
    setIsSyncing(true);
    try {
      await Promise.all([
        syncOfflinePricebook({ force: true }),
        syncMembershipData(),
      ]);
    } finally {
      setIsSyncing(false);
      refresh();
    }
  }, [syncOfflinePricebook, syncMembershipData, refresh]);

  const resetDatabase = useCallback(async () => {
    setOptedIn(false);
    await resetDb();
  }, [resetDb, setOptedIn]);

  return {
    isLoading,
    isSyncing,
    dbName: DB_NAME,
    collections,
    storage,
    lookupsSync,
    modifiersSync,
    pricebookSync,
    membershipDataSync,
    isOptedIn,
    canOptIn,
    setOptedIn,
    syncAllNow,
    resetDatabase,
  };
};
